import { useEffect, useId, useMemo, useRef, useState, type ReactElement } from 'react';
import { Copy, X } from 'lucide-react';
import type { Reconciliation } from '@molt/shared';
import { Button, Dialog, EmptyState, IconButton, Notice, TextField } from '@/components';
import type {
  DestinationBoardOption,
  DestinationListOption,
} from '@/lib/cross-board-move';
import {
  useInterfaceLanguage,
  type InterfaceLanguageContextValue,
} from '@/lib/interface-language';
import { MoveReconciliationReport } from './MoveReconciliationReport';

export interface ListCopyDialogSubmit {
  readonly boardId: string;
  readonly previousListId: string | null;
  readonly nextListId: string | null;
  readonly title: string;
  readonly titleEdited: boolean;
}

export interface ListCopyDialogProps {
  readonly open: boolean;
  readonly subjectTitle: string;
  readonly currentBoardId: string;
  readonly currentListId: string;
  readonly boards: readonly DestinationBoardOption[];
  readonly listsByBoardId: ReadonlyMap<string, readonly DestinationListOption[]>;
  readonly loading?: boolean | undefined;
  readonly busy?: boolean | undefined;
  readonly result?: Reconciliation | null | undefined;
  readonly cardsCopied?: number | undefined;
  readonly onCancel: () => void;
  readonly onCopy: (input: ListCopyDialogSubmit) => void;
}

type ListCopyPlacement = 'top' | 'bottom' | `after:${string}`;

export function ListCopyDialog({
  open,
  subjectTitle,
  currentBoardId,
  currentListId,
  boards,
  listsByBoardId,
  loading,
  busy,
  result,
  cardsCopied,
  onCancel,
  onCopy,
}: ListCopyDialogProps): ReactElement | null {
  const { t } = useInterfaceLanguage();
  const titleField = useRef<HTMLInputElement | null>(null);
  const formId = useId();
  const boardSelectId = useId();
  const positionId = useId();
  const cleanTitle =
    subjectTitle.trim() === '' ? t('copyMove.untitledList') : subjectTitle;

  const [title, setTitle] = useState(cleanTitle);
  const [titleEdited, setTitleEdited] = useState(false);
  const [selectedBoardId, setSelectedBoardId] = useState('');
  const [placement, setPlacement] = useState<ListCopyPlacement>('bottom');

  const defaultBoardId = useMemo(() => {
    if (boards.some((board) => board.id === currentBoardId)) return currentBoardId;
    return boards[0]?.id ?? '';
  }, [boards, currentBoardId]);

  const selectedBoard =
    boards.find((board) => board.id === selectedBoardId) ??
    boards.find((board) => board.id === defaultBoardId) ??
    null;
  const availableLists =
    selectedBoard === null ? [] : (listsByBoardId.get(selectedBoard.id) ?? []);

  useEffect(() => {
    if (!open) return;
    setTitle(cleanTitle);
    setTitleEdited(false);
    setSelectedBoardId(defaultBoardId);
  }, [cleanTitle, defaultBoardId, open]);

  useEffect(() => {
    if (!open) return;
    setPlacement(
      defaultListCopyPlacement(
        availableLists,
        currentListId,
        selectedBoard?.id === currentBoardId,
      ),
    );
  }, [availableLists, currentBoardId, currentListId, open, selectedBoard?.id]);

  const placementChoices = useMemo(
    () => listPlacementChoices(t, availableLists),
    [availableLists, t],
  );

  if (!open) return null;

  const controlsDisabled = busy === true || loading === true;
  const titleError = title.trim().length === 0 ? t('copyMove.titleRequired') : null;
  const confirmDisabled =
    controlsDisabled || selectedBoard === null || titleError !== null;
  const disabledReason =
    loading === true
      ? t('copyMove.loadingBoards')
      : busy === true
        ? t('copyMove.list.copying')
        : selectedBoard === null
          ? t('copyMove.chooseBoardFirst')
          : (titleError ?? undefined);
  const disabledReasonProps = disabledReason ? { disabledReason } : {};
  const busyProps = busy === undefined ? {} : { busy };
  const copiedSummary = cardsCopiedSummary(t, cardsCopied);

  function submitCopy() {
    if (selectedBoard === null || title.trim().length === 0 || controlsDisabled) {
      return;
    }

    const neighbours = listCopyNeighbours(availableLists, placement);
    onCopy({
      boardId: selectedBoard.id,
      previousListId: neighbours.previousListId,
      nextListId: neighbours.nextListId,
      title: title.trim(),
      titleEdited,
    });
  }

  return (
    <Dialog
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel();
      }}
      size="md"
      title={t('copyMove.list.title', { title: cleanTitle })}
      description={t('copyMove.list.description')}
      initialFocus={titleField}
      closeControl={
        <IconButton label={t('copyMove.closeCopy')} size="sm" variant="quiet">
          <X aria-hidden="true" className="size-4" />
        </IconButton>
      }
      footer={
        <>
          <Button size="sm" variant="secondary" onClick={onCancel}>
            {result !== null && result !== undefined
              ? t('copyMove.done')
              : t('copyMove.cancel')}
          </Button>
          <Button
            size="sm"
            variant="primary"
            iconStart={<Copy aria-hidden="true" className="size-4" />}
            busyLabel={t('copyMove.list.busy')}
            disabled={confirmDisabled}
            type="submit"
            form={formId}
            {...busyProps}
            {...disabledReasonProps}
          >
            {t('copyMove.list.submit')}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className="flex flex-col gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          submitCopy();
        }}
      >
        {result !== null && result !== undefined ? (
          <MoveReconciliationReport
            reconciliation={result}
            summary={copiedSummary ?? t('copyMove.list.copiedTitle')}
            tone="result"
          />
        ) : copiedSummary !== undefined ? (
          <Notice
            tone="info"
            title={t('copyMove.list.copiedTitle')}
            body={copiedSummary}
          />
        ) : null}

        <TextField
          ref={titleField}
          label={t('copyMove.copyTitleLabel')}
          value={title}
          disabled={controlsDisabled}
          onChange={(event) => {
            setTitleEdited(true);
            setTitle(event.currentTarget.value);
          }}
          {...(titleError === null ? {} : { error: titleError })}
        />

        {boards.length === 0 ? (
          <EmptyState
            tone="neutral"
            headingLevel={3}
            title={t('copyMove.noBoards.title')}
            body={t('copyMove.noBoards.body')}
          />
        ) : (
          <>
            <div className="flex flex-col gap-1">
              <label htmlFor={boardSelectId} className="text-sm font-medium text-ink">
                {t('copyMove.destinationBoardLabel')}
              </label>
              <select
                id={boardSelectId}
                value={selectedBoard?.id ?? ''}
                disabled={controlsDisabled}
                onChange={(event) => setSelectedBoardId(event.currentTarget.value)}
                className="h-9 rounded-card border border-border-strong bg-surface-raised px-3 text-card text-ink focus:border-accent disabled:cursor-not-allowed disabled:text-ink-muted"
              >
                {boards.map((board) => (
                  <option key={board.id} value={board.id}>
                    {t('copyMove.boardOption', {
                      board: board.title,
                      workspace: board.workspaceName,
                    })}
                  </option>
                ))}
              </select>
            </div>

            <fieldset className="flex flex-col gap-2" aria-labelledby={positionId}>
              <legend id={positionId} className="text-sm font-medium text-ink">
                {t('copyMove.positionLabel')}
              </legend>
              <div className="flex max-h-48 flex-col gap-1 overflow-y-auto pr-1">
                {placementChoices.map((choice) => (
                  <label
                    key={choice.value}
                    className="flex items-center gap-2 rounded-card px-2 py-1 text-sm text-ink hover:bg-surface-raised"
                  >
                    <input
                      type="radio"
                      name="list-copy-placement"
                      value={choice.value}
                      checked={placement === choice.value}
                      disabled={controlsDisabled}
                      onChange={() => setPlacement(choice.value)}
                    />
                    <span>{choice.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          </>
        )}
      </form>
    </Dialog>
  );
}

/** ⚠ `t` first and required — a module helper cannot call the hook. */
function listPlacementChoices(
  t: InterfaceLanguageContextValue['t'],
  lists: readonly DestinationListOption[],
): readonly { value: ListCopyPlacement; label: string }[] {
  if (lists.length === 0) {
    return [{ value: 'bottom', label: t('copyMove.list.position.first') }];
  }

  return [
    { value: 'top', label: t('copyMove.list.position.top') },
    { value: 'bottom', label: t('copyMove.list.position.bottom') },
    ...lists.map((list) => ({
      value: `after:${list.id}` as const,
      label: t('copyMove.list.position.after', {
        list: list.title.trim() === '' ? t('copyMove.untitledList') : list.title,
      }),
    })),
  ];
}

function defaultListCopyPlacement(
  lists: readonly DestinationListOption[],
  currentListId: string,
  sameBoard: boolean,
): ListCopyPlacement {
  return sameBoard && lists.some((list) => list.id === currentListId)
    ? (`after:${currentListId}` as const)
    : 'bottom';
}

function listCopyNeighbours(
  lists: readonly DestinationListOption[],
  placement: ListCopyPlacement,
): { previousListId: string | null; nextListId: string | null } {
  if (placement === 'top') {
    return { previousListId: null, nextListId: lists[0]?.id ?? null };
  }

  if (placement === 'bottom') {
    return { previousListId: lists.at(-1)?.id ?? null, nextListId: null };
  }

  const previousListId = placement.slice('after:'.length);
  const previousIndex = lists.findIndex((list) => list.id === previousListId);

  if (previousIndex === -1) {
    return { previousListId: lists.at(-1)?.id ?? null, nextListId: null };
  }

  return {
    previousListId,
    nextListId: lists[previousIndex + 1]?.id ?? null,
  };
}

/**
 * ⚠ ONE PLURAL KEY AT TWO NODE KINDS. When a reconciliation report is rendered
 * this sentence is its `summary`, which the report paints as a `Notice` TITLE;
 * when it is not, the same sentence is a `Notice` BODY. Mutually exclusive
 * branches, so one key — and the count is passed TWICE, once into `values` for
 * the `{count}` slot and once as the arm selector, because `interfaceMessage`
 * defaults `count` to 0 and would silently render the `other` form at one.
 */
function cardsCopiedSummary(
  t: InterfaceLanguageContextValue['t'],
  cardsCopied: number | undefined,
): string | undefined {
  if (cardsCopied === undefined) return undefined;
  return t('copyMove.list.copiedSummary', { count: cardsCopied }, cardsCopied);
}
